import { useEffect, useState } from "react";
import { Plus, ChevronRight, CalendarClock, GripVertical, Clock3, AlertTriangle, UserRound, Target, CircleDollarSign, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, FAMILLES, famColor } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ETAPES = [
  "Prospection",
  "Contacté",
  "Intéressé",
  "RDV",
  "Opportunité",
  "Devis",
  "Négociation",
  "Signé",
  "Perdu",
];

const ETAPE_ACCENT = {
  "Prospection": "#64748B",
  "Contacté": "#38BDF8",
  "Intéressé": "#06B6D4",
  "RDV": "#8B5CF6",
  "Opportunité": "#A78BFA",
  "Devis": "#3B82F6",
  "Négociation": "#F59E0B",
  "Signé": "#10B981",
  "Perdu": "#EF4444",
};

export default function PipelineKanban() {
  const { canSeeMargins } = useViewRole();
  const [opps, setOpps] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [clients, setClients] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [deleteOpp, setDeleteOpp] = useState(null);
  const [form, setForm] = useState({
    titre: "",
    client_nom: "",
    client_id: "",
    siren: "",
    valeur_ht: "",
    marge_estimee_ht: "",
    etape: "Prospection",
    probabilite_pct: "10",
    echeance: "",
    famille_principale: FAMILLES[0],
    commercial: "",
    source: "",
    prochaine_action: "",
    prochaine_date: "",
    priorite: "Normale"
  });

  const load = () => {
    api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {});
    api.get("/familles").then((r) => setFamilles(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const familleNames = familles.length ? familles.map((f) => f.nom) : FAMILLES;

  const onDrop = async (etape) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const opp = opps.find((o) => o.id === id);
    if (!opp || opp.etape === etape) return;
    setOpps((prev) => prev.map((o) => (o.id === id ? { ...o, etape } : o)));
    try { await api.patch(`/opportunities/${id}/etape`, { etape }); } catch { load(); }
  };

  const move = async (opp, dir) => {
    const idx = ETAPES.indexOf(opp.etape);
    const next = ETAPES[Math.min(ETAPES.length - 1, idx + dir)];
    if (next === opp.etape) return;
    setOpps((prev) => prev.map((o) => (o.id === opp.id ? { ...o, etape: next } : o)));
    try {
      await api.patch(`/opportunities/${opp.id}/etape`, { etape: next });
    } catch { load(); }
  };

  const editOpportunity = (opp) => {
    setEditingOpp(opp);
    setForm({
      titre: opp.titre || "",
      client_nom: opp.client_nom || "",
      client_id: opp.client_id || "",
      siren: opp.siren || "",
      valeur_ht: opp.valeur_ht ?? "",
      marge_estimee_ht: opp.marge_estimee_ht ?? "",
      etape: opp.etape || "Prospection",
      probabilite_pct: opp.probabilite_pct ?? "10",
      echeance: opp.echeance || "",
      famille_principale: opp.famille_principale || familleNames[0] || FAMILLES[0],
      commercial: opp.commercial || "",
      source: opp.source || "",
      prochaine_action: opp.prochaine_action || "",
      prochaine_date: opp.prochaine_date || "",
      priorite: opp.priorite || "Normale",
    });
    setOpen(true);
  };

  const confirmDeleteOpportunity = async () => {
    if (!deleteOpp) return;

    try {
      await api.delete(`/opportunities/${deleteOpp.id}`);
      toast.success("Opportunité supprimée");
      setDeleteOpp(null);

      if (editingOpp?.id === deleteOpp.id) {
        setEditingOpp(null);
        setOpen(false);
      }

      load();
    } catch {
      toast.error("Impossible de supprimer l’opportunité");
    }
  };

  const updateOpportunity = async () => {
    if (!editingOpp) return;

    if (!form.titre) {
      return toast.error("Titre requis");
    }

    try {
      await api.put(`/opportunities/${editingOpp.id}`, {
        ...form,
        valeur_ht: Number(form.valeur_ht) || 0,
        marge_estimee_ht: Number(form.marge_estimee_ht) || 0,
        probabilite_pct: Number(form.probabilite_pct) || 0,
      });

      toast.success("Opportunité modifiée");
      setOpen(false);
      setEditingOpp(null);
      load();
    } catch {
      toast.error("Erreur lors de la modification");
    }
  };

  const create = async () => {
    if (!form.titre) return toast.error("Titre requis");
    try {
      await api.post("/opportunities", {
        ...form,
        valeur_ht: Number(form.valeur_ht) || 0,
        marge_estimee_ht: Number(form.marge_estimee_ht) || 0,
        probabilite_pct: Number(form.probabilite_pct) || 0,
      });
      toast.success("Opportunité ajoutée au pipeline");
      setOpen(false);
      setForm({ ...form, titre: "", client_nom: "", valeur_ht: "", marge_estimee_ht: "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  const openOpps = opps.filter((o) => !["Signé", "Perdu"].includes(o.etape));
  const pipelineValue = openOpps.reduce(
    (sum, o) => sum + Number(o.valeur_ht || 0),
    0
  );
  const weightedValue = openOpps.reduce(
    (sum, o) =>
      sum +
      (Number(o.valeur_ht || 0) * Number(o.probabilite_pct || 0)) / 100,
    0
  );
  const estimatedMargin = openOpps.reduce(
    (sum, o) => sum + Number(o.marge_estimee_ht || 0),
    0
  );
  const noNextAction = openOpps.filter((o) => !o.prochaine_action).length;

  return (
    <div className="space-y-6" data-testid="pipeline-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Pipeline ouvert
          </div>
          <div className="mt-1 text-xl font-bold text-[#102A4C] font-mono-metric">
            {eur(pipelineValue)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {openOpps.length} affaire{openOpps.length > 1 ? "s" : ""}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Pipeline pondéré
          </div>
          <div className="mt-1 text-xl font-bold text-blue-600 font-mono-metric">
            {eur(weightedValue)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Valeur × probabilité
          </div>
        </div>

        {canSeeMargins && (
          <div className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Marge estimée
            </div>
            <div className="mt-1 text-xl font-bold text-emerald-600 font-mono-metric">
              {eur(estimatedMargin)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Sur les affaires ouvertes
            </div>
          </div>
        )}

        <div className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Actions à sécuriser
          </div>
          <div className="mt-1 text-xl font-bold text-amber-500 font-mono-metric">
            {noNextAction}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Affaire{noNextAction > 1 ? "s" : ""} sans prochaine action
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Pipeline Opportunités</h1>
          <p className="text-slate-400 text-sm mt-1">Flux des affaires avec CA et marge cumulés par étape.</p>
        </div>
        <Button data-testid="add-opportunity-button" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle opportunité
        </Button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {ETAPES.map((etape) => {
          const list = opps.filter((o) => o.etape === etape);
          const totalCa = list.reduce((s, o) => s + (o.valeur_ht || 0), 0);
          const totalMarge = list.reduce((s, o) => s + (o.marge_estimee_ht || 0), 0);
          return (
            <div
              key={etape}
              className="w-72 shrink-0"
              data-testid={`pipeline-column-${etape}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(etape)}
            >
              <div className="rounded-t-xl bg-[#111827] border border-[#1E293B] p-3" style={{ borderTop: `3px solid ${ETAPE_ACCENT[etape]}` }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{etape}</span>
                  <span className="text-xs text-slate-500 font-mono-metric">{list.length}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-xs">
                  <span className="font-mono-metric text-slate-300">{eur(totalCa)}</span>
                  {canSeeMargins && <span className="font-mono-metric text-emerald-400">{eur(totalMarge)}</span>}
                </div>
              </div>
              <div className="rounded-b-xl bg-slate-50 border border-t-0 border-slate-200 p-2 space-y-2 min-h-[120px]">
                {list.map((o) => (
                  <div
                    key={o.id}
                    draggable
                    onDragStart={() => setDragId(o.id)}
                    onDragEnd={() => setDragId(null)}
                    className="rounded-xl bg-[#111827] border border-[#1E293B] p-3 hover:border-slate-600 transition-colors cursor-grab active:cursor-grabbing"
                    data-testid={`opp-card-${o.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-3.5 w-3.5 text-slate-600 mt-1 shrink-0" />

                      <span
                        className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0"
                        style={{
                          background: famColor(
                            o.famille_principale,
                            familles
                          ),
                        }}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold leading-tight">
                          {o.titre}
                        </div>

                        {o.client_nom && (
                          <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400">
                            <UserRound className="h-3 w-3" />
                            <span className="truncate">{o.client_nom}</span>
                          </div>
                        )}
                      </div>

                      {o.priorite && (
                        <span
                          className={
                            "text-[9px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 " +
                            (
                              o.priorite === "Haute"
                                ? "bg-red-500/15 text-red-400"
                                : o.priorite === "Basse"
                                  ? "bg-slate-500/15 text-slate-400"
                                  : "bg-amber-500/15 text-amber-400"
                            )
                          }
                        >
                          {o.priorite}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-2">
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">
                          Valeur
                        </div>
                        <div className="font-mono-metric text-sm font-semibold text-slate-100 mt-0.5">
                          {eur(o.valeur_ht)}
                        </div>
                      </div>

                      <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-2">
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">
                          Probabilité
                        </div>
                        <div className="font-mono-metric text-sm font-semibold text-sky-300 mt-0.5">
                          {o.probabilite_pct || 0} %
                        </div>
                      </div>
                    </div>

                    {canSeeMargins && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-emerald-400 font-mono-metric">
                        <CircleDollarSign className="h-3.5 w-3.5" />
                        Marge est. {eur(o.marge_estimee_ht)}
                      </div>
                    )}

                    {o.famille_principale && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-slate-400">
                        <Target className="h-3 w-3" />
                        {o.famille_principale}
                      </div>
                    )}

                    <div className="mt-3 rounded-lg bg-[#0B0F17] border border-[#1E293B] p-2">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">
                        Prochaine action
                      </div>

                      {o.prochaine_action ? (
                        <div className="mt-1 text-xs font-semibold text-slate-200">
                          {o.prochaine_action}
                        </div>
                      ) : (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-400">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Aucune action planifiée
                        </div>
                      )}

                      {o.prochaine_date && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500">
                          <CalendarClock className="h-3 w-3" />
                          {o.prochaine_date}
                        </div>
                      )}
                    </div>

                    <div className="mt-2 space-y-2 text-xs">
                      <div className="flex items-center justify-between text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="h-3 w-3" />
                          {o.date_derniere_activite
                            ? new Date(o.date_derniere_activite).toLocaleDateString(
                                "fr-FR"
                              )
                            : "Aucune activité"}
                        </span>

                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => editOpportunity(o)}
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-blue-400 font-semibold"
                            data-testid={`opp-edit-${o.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                            Modifier
                          </button>

                          <button
                            type="button"
                            onClick={() => setDeleteOpp(o)}
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-red-400 font-semibold"
                            data-testid={`opp-delete-${o.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                            Supprimer
                          </button>
                        </div>
                      </div>

                      {ETAPES.indexOf(o.etape) < ETAPES.length - 1 && (
                        <button
                          data-testid={`opp-advance-${o.id}`}
                          onClick={() => move(o, 1)}
                          className="w-full justify-center inline-flex items-center gap-1 rounded-md bg-amber-50 py-1.5 text-amber-600 hover:bg-amber-100 font-semibold"
                        >
                          Avancer <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-head">{editingOpp ? "Modifier l’opportunité" : "Nouvelle opportunité"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Titre</Label>
              <Input data-testid="opp-input-titre" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Client / Prospect</Label>
                <Select
                  value={form.client_id || "none"}
                  onValueChange={(v) => {
                    if (v === "none") {
                      setForm({
                        ...form,
                        client_id: "",
                        client_nom: "",
                        siren: "",
                      });
                      return;
                    }

                    const client = clients.find((c) => c.id === v);
                    setForm({
                      ...form,
                      client_id: client?.id || "",
                      client_nom: client?.nom || "",
                      siren: client?.siren || "",
                    });
                  }}
                >
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]">
                    <SelectValue placeholder="Sélectionner un client ou prospect" />
                  </SelectTrigger>

                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    <SelectItem value="none">
                      Aucun client lié
                    </SelectItem>

                    {clients
                      .filter((c) => !c.is_demo)
                      .sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr"))
                      .map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.nom}
                          {client.is_prospect ? " — Prospect" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                {form.siren && (
                  <div className="text-[10px] text-slate-500">
                    SIREN : {form.siren}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Échéance</Label>
                <Input type="date" value={form.echeance} onChange={(e) => setForm({ ...form, echeance: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Valeur HT (€)</Label>
                <Input type="number" data-testid="opp-input-valeur" value={form.valeur_ht} onChange={(e) => setForm({ ...form, valeur_ht: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Marge estimée HT (€)</Label>
                <Input type="number" value={form.marge_estimee_ht} onChange={(e) => setForm({ ...form, marge_estimee_ht: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Étape</Label>
                <Select value={form.etape} onValueChange={(v) => setForm({ ...form, etape: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {ETAPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Famille</Label>
                <Select value={form.famille_principale} onValueChange={(v) => setForm({ ...form, famille_principale: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {familleNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Priorité</Label>
                <Select
                  value={form.priorite}
                  onValueChange={(v) => setForm({ ...form, priorite: v })}
                >
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    <SelectItem value="Basse">Basse</SelectItem>
                    <SelectItem value="Normale">Normale</SelectItem>
                    <SelectItem value="Haute">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Source</Label>
                <Select
                  value={form.source || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, source: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]">
                    <SelectValue placeholder="Origine de l'affaire" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    <SelectItem value="none">Non renseignée</SelectItem>
                    <SelectItem value="Business Hunter">Business Hunter</SelectItem>
                    <SelectItem value="Client">Client</SelectItem>
                    <SelectItem value="Prospection">Prospection</SelectItem>
                    <SelectItem value="Magasin">Magasin</SelectItem>
                    <SelectItem value="Recommandation">Recommandation</SelectItem>
                    <SelectItem value="Partenaire">Partenaire</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Prochaine action</Label>
                <Input
                  value={form.prochaine_action}
                  onChange={(e) =>
                    setForm({ ...form, prochaine_action: e.target.value })
                  }
                  placeholder="Ex. Appeler le prospect"
                  className="bg-[#0B0F17] border-[#1E293B]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Date de prochaine action</Label>
                <Input
                  type="date"
                  value={form.prochaine_date}
                  onChange={(e) =>
                    setForm({ ...form, prochaine_date: e.target.value })
                  }
                  className="bg-[#0B0F17] border-[#1E293B]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button
              data-testid="opp-save-button"
              onClick={editingOpp ? updateOpportunity : create}
              className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold"
            >
              {editingOpp ? "Enregistrer les modifications" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteOpp)}
        onOpenChange={(value) => {
          if (!value) setDeleteOpp(null);
        }}
      >
        <DialogContent className="bg-white border-slate-200 text-[#102A4C]">
          <DialogHeader>
            <DialogTitle className="font-head">
              Supprimer l’opportunité ?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Vous êtes sur le point de supprimer définitivement :
            </p>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="font-semibold text-[#102A4C]">
                {deleteOpp?.titre || "Opportunité"}
              </div>

              {deleteOpp?.client_nom && (
                <div className="text-xs text-slate-500 mt-1">
                  {deleteOpp.client_nom}
                </div>
              )}
            </div>

            <p className="text-xs text-red-600">
              Cette action est définitive.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpp(null)}
              className="text-slate-500"
            >
              Annuler
            </Button>

            <Button
              onClick={confirmDeleteOpportunity}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="opp-confirm-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
